import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { CheckSquare, MessageCircle, X, ArrowLeft, Plus, Paperclip, Send, Download, Search, Maximize2, Minimize2, Smile, Pencil, Trash2, ListTodo, Trello, ExternalLink, UserPlus, Check, Users, UserMinus, Crown } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import clsx from "clsx";
import {
  useConversations,
  useUnreadChatCount,
  useMessageableUsers,
  useStartConversation,
  useConversationMessages,
  useSendChatMessage,
  useMarkConversationRead,
  useEditChatMessage,
  useDeleteChatMessage,
  useDeleteConversation, useDeleteConversations,
  useAddParticipants,
  useConversationDetail,
  useRemoveParticipant,
  downloadChatAttachment,
} from "../../api/chat";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { useChatLauncher } from "../../context/ChatLauncherContext";
import { extractApiError } from "../../lib/apiClient";
import { Avatar, Spinner, EmptyState } from "../ui/primitives";
import { ConfirmDialog } from "../ui/ConfirmDialog";

const EDIT_WINDOW_MS = 60_000;

const STICKERS = [
  "😀", "😂", "😍", "😎", "🤔", "😭", "😡", "🥳",
  "😴", "😅", "🥰", "😇", "👍", "👎", "🙏", "👏",
  "🙌", "🤝", "👋", "💪", "❤️", "🔥", "💯", "🎉",
  "🚀", "⭐", "✅", "❌",
];

function isStickerOnly(text?: string | null) {
  if (!text) return false;
  const trimmed = text.trim();
  if (!trimmed || trimmed.length > 16) return false;
  return /^(\p{Extended_Pictographic}|\s)+$/u.test(trimmed);
}

function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

type View = { screen: "list" } | { screen: "new" } | { screen: "thread"; conversationId: string; title: string; isGroup?: boolean };

/** Floating 1:1 chat widget, mounted once at the layout level so it persists
 * across every page. Polling-based (no WebSocket infra) — matches the
 * Notifications pattern already used elsewhere in this app. */
/**
 * Request 1189 — the floating chat button: an orb inside a turning aurora ring with a soft breathing glow. When a message
 * arrives a paper plane swoops in along a glowing trail, the orb squashes and springs back (jelly), shock-waves and sparks
 * burst out and the bubble pops; while something is unread the ring spins faster, the badge pings and the orb gives a little
 * nudge every few seconds. The bubble turns into a cross when the window is open.
 */
const SPARKS = [0, 36, 72, 108, 144, 180, 216, 252, 288, 324];
const SPARK_COLORS = ["#6366f1", "#ec4899", "#06b6d4", "#a855f7", "#f59e0b"];
function ChatLauncher({ open, unread, burst, onClick }: { open: boolean; unread: number; burst: number; onClick: () => void }) {
  const show = burst > 0 && !open;
  return (
    <div className={clsx("relative h-14 w-14", !open && unread > 0 && "cl-nudge")}>
      {/* aurora ring + glow */}
      <span className={clsx("cl-aurora pointer-events-none absolute -inset-[3px] rounded-full opacity-90 blur-[1px]", unread > 0 && !open && "cl-aurora-fast")} />
      <span className="cl-glow pointer-events-none absolute -inset-2 rounded-full bg-gradient-to-br from-brand-500 to-pink-500 blur-xl" />

      {/* the arrival show — re-keyed so it plays again for every new message */}
      {show && (
        <span key={burst} className="pointer-events-none absolute inset-0">
          <svg className="absolute -left-[130px] -top-[120px] h-[150px] w-[150px] overflow-visible" viewBox="0 0 150 150">
            <path d="M8 8 C 40 20, 70 70, 140 140" fill="none" stroke="url(#cl-trail-grad)" strokeWidth="2" strokeLinecap="round" strokeDasharray="4 6" className="cl-trail" />
            <defs>
              <linearGradient id="cl-trail-grad" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#ec4899" stopOpacity="0" />
                <stop offset="100%" stopColor="#6366f1" />
              </linearGradient>
            </defs>
          </svg>
          <span className="cl-plane absolute left-1/2 top-1/2 -ml-3 -mt-3 flex h-6 w-6 items-center justify-center rounded-full bg-white text-brand-600 shadow-lg">
            <Send className="h-3.5 w-3.5" />
          </span>
          {[0, 0.18, 0.36].map((d) => (
            <span key={d} className="cl-wave absolute inset-0 rounded-full border-2 border-violet-400" style={{ animationDelay: `${0.55 + d}s` }} />
          ))}
          {SPARKS.map((a, i) => (
            <span
              key={a}
              className="cl-spark absolute left-1/2 top-1/2 -ml-1 -mt-1 h-2 w-2 rounded-full"
              style={{ ["--a" as string]: `${a}deg`, ["--d" as string]: `${40 + (i % 3) * 10}px`, background: SPARK_COLORS[i % SPARK_COLORS.length], boxShadow: `0 0 6px ${SPARK_COLORS[i % SPARK_COLORS.length]}` }}
            />
          ))}
        </span>
      )}

      <button
        key={show ? `b${burst}` : "b"}
        onClick={onClick}
        className={clsx(
          "group relative flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-brand-500 via-indigo-600 to-violet-700 text-white shadow-xl shadow-brand-700/40 ring-2 ring-white/30 transition-transform duration-300 hover:-translate-y-0.5 hover:scale-105",
          show && "cl-jelly"
        )}
        aria-label={open ? "Close chat" : `Open chat${unread ? `, ${unread} unread` : ""}`}
      >
        <span className="pointer-events-none absolute inset-0 rounded-full bg-gradient-to-b from-white/25 to-transparent" />
        {/* bubble ↔ cross */}
        <span className={clsx("absolute transition-all duration-500 ease-[cubic-bezier(.34,1.56,.64,1)]", open ? "rotate-90 scale-0 opacity-0" : "rotate-0 scale-100 opacity-100")}>
          <span className={clsx("relative block", show && "cl-pop")}>
            <svg viewBox="0 0 24 24" className="h-7 w-7 transition-transform duration-300 group-hover:-rotate-6" fill="none">
              <path d="M12 3.5c4.97 0 9 3.36 9 7.5s-4.03 7.5-9 7.5c-.98 0-1.93-.13-2.82-.38L4.5 20l1.2-3.6C4.03 15.06 3 13.13 3 11c0-4.14 4.03-7.5 9-7.5Z" fill="white" fillOpacity=".95" />
              {[8.5, 12, 15.5].map((cx, i) => (
                <circle key={cx} cx={cx} cy="11" r="1.25" fill="#6366f1" className={clsx((unread > 0 || show) && "cl-dot")} style={{ animationDelay: `${i * 0.15}s`, transformBox: "fill-box", transformOrigin: "center" }} />
              ))}
            </svg>
          </span>
        </span>
        <span className={clsx("absolute transition-all duration-500 ease-[cubic-bezier(.34,1.56,.64,1)]", open ? "rotate-0 scale-100 opacity-100" : "-rotate-90 scale-0 opacity-0")}>
          <X className="h-6 w-6" />
        </span>

        {!open && unread > 0 && (
          <span className="absolute -right-1 -top-1">
            <span className="absolute inset-0 animate-ping rounded-full bg-rose-400 opacity-60 motion-reduce:animate-none" />
            <span key={unread} className="relative flex h-5 min-w-[20px] items-center justify-center rounded-full bg-gradient-to-br from-rose-500 to-red-600 px-1 text-[11px] font-bold text-white shadow-md ring-2 ring-white animate-badge-bounce-in">
              {unread > 99 ? "99+" : unread}
            </span>
          </span>
        )}
      </button>
    </div>
  );
}

export const ChatWidget: React.FC = () => {
  const { user } = useAuth();
  const { push } = useToast();
  const [open, setOpen] = useState(false);
  const [maximized, setMaximized] = useState(false);
  const [view, setView] = useState<View>({ screen: "list" });
  const [userSearch, setUserSearch] = useState("");
  const [draft, setDraft] = useState("");
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [showStickers, setShowStickers] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState("");
  const [pendingDeleteMessageId, setPendingDeleteMessageId] = useState<string | null>(null);
  const [pendingDeleteConversation, setPendingDeleteConversation] = useState(false);
  // request 1191: pick conversations (or all of them) in the inbox and clear them in one go
  const [selecting, setSelecting] = useState(false);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [pendingBulkDelete, setPendingBulkDelete] = useState(false);
  const [showAddPeople, setShowAddPeople] = useState(false);
  const [addPeopleSearch, setAddPeopleSearch] = useState("");
  const [selectedToAdd, setSelectedToAdd] = useState<Set<string>>(new Set());
  const [showMembers, setShowMembers] = useState(false);
  const [pulseActive, setPulseActive] = useState(false);
  // request 1189: each arrival replays the launcher's show (burst), and asks for a peek at who wrote
  const [burst, setBurst] = useState(0);
  const [peekWanted, setPeekWanted] = useState(false);
  const prevUnreadRef = useRef<number | null>(null);
  // Which message ids just appeared in the open thread — briefly true so
  // they slide/fade in (and, for an incoming one, glow) instead of just
  // popping into place; cleared once the cue has played.
  const [newMessageIds, setNewMessageIds] = useState<Set<string>>(new Set());
  const prevMessageIdsRef = useRef<Set<string>>(new Set());
  const fileInputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const stickerRef = useRef<HTMLDivElement>(null);
  const addPeopleRef = useRef<HTMLDivElement>(null);
  const membersRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const { data: unreadCount } = useUnreadChatCount();

  // A brief "new message" cue on the floating button — pulse + ring + typing
  // dots — the moment the unread count rises while the panel is closed
  // (polling-based, so this is the closest thing to a "receiving" signal we
  // have). Also fires the moment the app is opened/reloaded and there are
  // already unread messages waiting — not just on a rise from a baseline —
  // so refreshing the page with unread messages still surfaces the cue
  // instead of silently showing just the plain badge.
  useEffect(() => {
    if (unreadCount === undefined) return;
    const prev = prevUnreadRef.current;
    prevUnreadRef.current = unreadCount;
    if (open) return;
    const isFirstLoad = prev === null;
    const rose = !isFirstLoad && unreadCount > prev!;
    const arrivedWithUnread = isFirstLoad && unreadCount > 0;
    if (rose || arrivedWithUnread) {
      setPulseActive(true);
      setBurst((b) => b + 1);
      setPeekWanted(true);
      const timer = setTimeout(() => setPulseActive(false), 2400);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unreadCount]);
  const { data: conversations, isLoading: conversationsLoading } = useConversations((open && view.screen === "list") || (peekWanted && !open));
  // the newest conversation with something unread — what the peek shows; it slips away after a few seconds
  const peek = React.useMemo(() => {
    if (!peekWanted || open || !conversations) return null;
    const c = [...(conversations as any[])]
      .filter((x) => x.unreadCount > 0)
      .sort((a, b) => +new Date(b.lastMessage?.createdAt ?? 0) - +new Date(a.lastMessage?.createdAt ?? 0))[0];
    if (!c) return null;
    const m = c.lastMessage;
    const text = !m ? "New message" : m.kind === "CARD" ? "📌 Shared a card" : isStickerOnly(m.body) ? `${m.body} sticker` : m.body || (m.hasAttachments ? "📎 Sent an attachment" : "New message");
    return { id: c.id as string, title: c.title as string, isGroup: !!c.isGroup, text: text as string, unread: c.unreadCount as number };
  }, [peekWanted, open, conversations]);
  useEffect(() => {
    if (!peekWanted) return;
    const t = setTimeout(() => setPeekWanted(false), 8000);
    return () => clearTimeout(t);
  }, [peekWanted, burst]);
  useEffect(() => {
    if (open) setPeekWanted(false);
  }, [open]);
  const { data: messageableUsers } = useMessageableUsers(userSearch, open && view.screen === "new");
  const startConversation = useStartConversation();
  const conversationId = view.screen === "thread" ? view.conversationId : null;
  const { data: messages, isLoading: messagesLoading } = useConversationMessages(conversationId);
  const sendMessage = useSendChatMessage(conversationId);
  const markRead = useMarkConversationRead();
  const editMessage = useEditChatMessage(conversationId);
  const deleteMessage = useDeleteChatMessage(conversationId);
  const deleteConversation = useDeleteConversation();
  const deleteConversations = useDeleteConversations();
  const addParticipants = useAddParticipants(conversationId);
  const { data: addPeopleCandidates } = useMessageableUsers(addPeopleSearch, open && view.screen === "thread" && showAddPeople);
  const { data: conversationDetail } = useConversationDetail(open && view.screen === "thread" && view.isGroup ? conversationId : null);
  const removeParticipant = useRemoveParticipant(conversationId);
  const { pendingOpen, clearPendingOpen } = useChatLauncher();

  // A "Discuss" button elsewhere in the app just created a group conversation
  // and wants this globally-mounted widget to jump straight to it.
  useEffect(() => {
    if (!pendingOpen) return;
    setOpen(true);
    setView({ screen: "thread", conversationId: pendingOpen.conversationId, title: pendingOpen.title, isGroup: pendingOpen.isGroup });
    clearPendingOpen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingOpen]);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      // ConfirmDialog renders via a portal into document.body, outside this
      // panel's DOM subtree — without this guard, clicking its buttons would
      // register as an "outside click" and close the whole widget.
      if ((e.target as Element)?.closest?.('[role="dialog"]')) return;
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (stickerRef.current && !stickerRef.current.contains(e.target as Node)) setShowStickers(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (addPeopleRef.current && !addPeopleRef.current.contains(e.target as Node)) setShowAddPeople(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (membersRef.current && !membersRef.current.contains(e.target as Node)) setShowMembers(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    setShowAddPeople(false);
    setAddPeopleSearch("");
    setSelectedToAdd(new Set());
    setShowMembers(false);
    // Opening a (possibly different) thread is not a "new arrival" — reset
    // so its full history doesn't all animate in at once.
    prevMessageIdsRef.current = new Set();
    setNewMessageIds(new Set());
  }, [conversationId]);

  // Diffs against the previous render's message ids so only messages that
  // just appeared (sent or received) get the arrival animation — a poll
  // refetch of an unchanged thread shouldn't replay it for everything.
  useEffect(() => {
    if (!messages) return;
    const currentIds = new Set<string>(messages.map((m: any) => m.id as string));
    const prevIds = prevMessageIdsRef.current;
    if (prevIds.size > 0) {
      const added = [...currentIds].filter((id) => !prevIds.has(id));
      if (added.length > 0) {
        setNewMessageIds(new Set(added));
        const timer = setTimeout(() => setNewMessageIds(new Set()), 900);
        prevMessageIdsRef.current = currentIds;
        return () => clearTimeout(timer);
      }
    }
    prevMessageIdsRef.current = currentIds;
  }, [messages]);

  useEffect(() => {
    if (conversationId) markRead.mutate(conversationId);
    // Only re-run when the thread or its message count changes — marking
    // read on every render (e.g. from markRead's own mutation state) would
    // loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, messages?.length]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ block: "end" });
  }, [messages?.length]);

  function openConversation(id: string, title: string, isGroup?: boolean) {
    setSelecting(false);
    setPicked(new Set());
    setView({ screen: "thread", conversationId: id, title, isGroup });
  }

  async function handleStartConversation(userId: string, name: string) {
    try {
      const conversation = await startConversation.mutateAsync(userId);
      setUserSearch("");
      openConversation(conversation.id, name);
    } catch (err) {
      push({ variant: "error", title: "Could not start conversation", description: extractApiError(err).message });
    }
  }

  function onFilesSelected(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    e.target.value = "";
    setPendingFiles((prev) => [...prev, ...files]);
  }

  function insertSticker(sticker: string) {
    setDraft((prev) => (prev ? `${prev} ${sticker}` : sticker));
    setShowStickers(false);
  }

  function startEdit(m: any) {
    setEditingId(m.id);
    setEditDraft(m.body ?? "");
  }

  function cancelEdit() {
    setEditingId(null);
    setEditDraft("");
  }

  async function saveEdit(messageId: string) {
    if (!editDraft.trim()) return;
    try {
      await editMessage.mutateAsync({ messageId, body: editDraft.trim() });
      cancelEdit();
    } catch (err) {
      push({ variant: "error", title: "Could not edit message", description: extractApiError(err).message });
    }
  }

  async function confirmDeleteMessage() {
    if (!pendingDeleteMessageId) return;
    try {
      await deleteMessage.mutateAsync(pendingDeleteMessageId);
      setPendingDeleteMessageId(null);
    } catch (err) {
      push({ variant: "error", title: "Could not delete message", description: extractApiError(err).message });
    }
  }

  function toggleSelectedToAdd(userId: string) {
    setSelectedToAdd((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  }

  async function submitAddParticipants() {
    if (selectedToAdd.size === 0) return;
    try {
      await addParticipants.mutateAsync([...selectedToAdd]);
      push({ variant: "success", title: "Added to the discussion." });
      setSelectedToAdd(new Set());
      setAddPeopleSearch("");
      setShowAddPeople(false);
    } catch (err) {
      push({ variant: "error", title: "Could not add people", description: extractApiError(err).message });
    }
  }

  async function handleRemoveParticipant(userId: string) {
    try {
      await removeParticipant.mutateAsync(userId);
      if (userId === user!.id) {
        push({ variant: "success", title: "You left the discussion." });
        setShowMembers(false);
        setView({ screen: "list" });
      } else {
        push({ variant: "success", title: "Removed from the discussion." });
      }
    } catch (err) {
      push({ variant: "error", title: "Could not remove", description: extractApiError(err).message });
    }
  }

  function stopSelecting() {
    setSelecting(false);
    setPicked(new Set());
  }
  async function confirmBulkDelete() {
    try {
      const r = await deleteConversations.mutateAsync([...picked]);
      setPendingBulkDelete(false);
      stopSelecting();
      if (r.failed) push({ variant: "error", title: `${r.failed} conversation${r.failed === 1 ? "" : "s"} could not be deleted`, description: `${r.done} cleared from your inbox.` });
      else push({ variant: "success", title: `${r.done} conversation${r.done === 1 ? "" : "s"} cleared from your inbox.` });
    } catch (err) {
      push({ variant: "error", title: "Could not delete conversations", description: extractApiError(err).message });
    }
  }

  async function confirmDeleteConversation() {
    if (!conversationId) return;
    try {
      await deleteConversation.mutateAsync(conversationId);
      setPendingDeleteConversation(false);
      setView({ screen: "list" });
    } catch (err) {
      push({ variant: "error", title: "Could not delete conversation", description: extractApiError(err).message });
    }
  }

  async function submitMessage(e: React.FormEvent) {
    e.preventDefault();
    if (!draft.trim() && pendingFiles.length === 0) return;
    try {
      await sendMessage.mutateAsync({ body: draft.trim() || undefined, files: pendingFiles });
      setDraft("");
      setPendingFiles([]);
    } catch (err) {
      push({ variant: "error", title: "Could not send message", description: extractApiError(err).message });
    }
  }

  if (!user) return null;

  const sizeControl = (
    <button
      onClick={() => setMaximized((m) => !m)}
      className="rounded-md p-1 text-slate-400 hover:bg-slate-100"
      aria-label={maximized ? "Minimize chat" : "Maximize chat"}
    >
      {maximized ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
    </button>
  );

  return (
    // z-[60] — deliberately above Drawer/Modal's z-50 (ui/Drawer.tsx,
    // ui/Modal.tsx). The "Discuss" button opens this widget from inside an
    // open Task Detail Drawer, which the user may keep open at the same
    // time — without this, the widget opened correctly in state but
    // rendered invisible underneath the still-open drawer.
    // While a Drawer/Modal is open and the chat panel is closed, the round button steps aside (see lib/overlayFlag.ts and
    // index.css) so it never covers the dialog's own buttons — e.g. "Raise invoice". Opening chat from inside a drawer ("Discuss")
    // still shows the panel on top.
    <div className={clsx("fixed bottom-20 right-4 z-[60] sm:bottom-4", !open && "chat-dock-idle")} ref={panelRef}>
      {open && (
        <div
          className={clsx(
            "cl-panel relative mb-3 flex max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl shadow-brand-900/15",
            maximized ? "h-[80vh] w-[50vw]" : "h-[30rem] w-[22rem]"
          )}
        >
          <span className="cl-shimmer pointer-events-none absolute inset-x-0 top-0 z-10 h-[3px] bg-gradient-to-r from-brand-500 via-pink-500 via-50% to-cyan-400" />
          {view.screen === "thread" ? (
            <div className="relative flex items-center gap-2 border-b border-slate-100 px-3 py-2.5">
              <button onClick={() => setView({ screen: "list" })} className="rounded-md p-1 text-slate-400 hover:bg-slate-100" aria-label="Back to conversations">
                <ArrowLeft className="h-4 w-4" />
              </button>
              <Avatar name={view.title} size="sm" />
              <p className="truncate text-sm font-semibold text-slate-900">{view.title}</p>
              <div className="ml-auto flex items-center gap-1">
                {view.isGroup && (
                  <button
                    onClick={() => setShowMembers((s) => !s)}
                    className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-brand-600"
                    aria-label="See who's in this discussion"
                  >
                    <Users className="h-4 w-4" />
                  </button>
                )}
                {view.isGroup && (
                  <button
                    onClick={() => setShowAddPeople((s) => !s)}
                    className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-brand-600"
                    aria-label="Add people to this discussion"
                  >
                    <UserPlus className="h-4 w-4" />
                  </button>
                )}
                <button
                  onClick={() => setPendingDeleteConversation(true)}
                  className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-red-500"
                  aria-label="Delete conversation"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
                {sizeControl}
              </div>
              <button onClick={() => setOpen(false)} className="rounded-md p-1 text-slate-400 hover:bg-slate-100" aria-label="Close chat">
                <X className="h-4 w-4" />
              </button>

              {showAddPeople && view.isGroup && (
                <div
                  ref={addPeopleRef}
                  className="absolute right-2 top-full z-10 mt-1 flex w-64 flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg"
                >
                  <div className="border-b border-slate-100 p-2">
                    <div className="relative">
                      <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                      <input
                        autoFocus
                        value={addPeopleSearch}
                        onChange={(e) => setAddPeopleSearch(e.target.value)}
                        placeholder="Search people…"
                        className="w-full rounded-lg border border-slate-200 bg-slate-50 py-1.5 pl-8 pr-2 text-sm text-slate-700 placeholder:text-slate-400 focus-visible:focus-ring"
                      />
                    </div>
                  </div>
                  <div className="max-h-48 overflow-y-auto">
                    {(addPeopleCandidates ?? []).length === 0 && (
                      <p className="px-3 py-4 text-center text-xs text-slate-400">No people found</p>
                    )}
                    {(addPeopleCandidates ?? []).map((u: any) => {
                      const isSelected = selectedToAdd.has(u.id);
                      return (
                        <button
                          key={u.id}
                          type="button"
                          onClick={() => toggleSelectedToAdd(u.id)}
                          className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-slate-50"
                        >
                          <Avatar name={u.name} size="xs" />
                          <span className="min-w-0 flex-1 truncate text-slate-700">{u.name}</span>
                          {isSelected && <Check className="h-3.5 w-3.5 shrink-0 text-brand-600" />}
                        </button>
                      );
                    })}
                  </div>
                  <div className="flex justify-end border-t border-slate-100 p-2">
                    <button
                      type="button"
                      onClick={submitAddParticipants}
                      disabled={selectedToAdd.size === 0 || addParticipants.isPending}
                      className="rounded-lg bg-brand-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-700 disabled:opacity-50"
                    >
                      Add{selectedToAdd.size > 0 ? ` (${selectedToAdd.size})` : ""}
                    </button>
                  </div>
                </div>
              )}

              {showMembers && view.isGroup && (
                <div
                  ref={membersRef}
                  className="absolute right-2 top-full z-10 mt-1 flex w-64 flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg"
                >
                  <p className="border-b border-slate-100 px-3 py-2 text-[11px] font-medium uppercase tracking-wide text-slate-400">
                    {conversationDetail ? `${conversationDetail.participants.length} people` : "People"}
                  </p>
                  <div className="max-h-56 overflow-y-auto">
                    {!conversationDetail && (
                      <div className="flex justify-center py-4">
                        <Spinner className="h-4 w-4" />
                      </div>
                    )}
                    {conversationDetail?.participants.map((p) => {
                      const isCreator = p.userId === conversationDetail.createdById;
                      const isSelf = p.userId === user.id;
                      const canRemove = isSelf || conversationDetail.createdById === user.id;
                      return (
                        <div key={p.userId} className="flex items-center gap-2 px-3 py-1.5 text-sm">
                          <Avatar name={p.name} size="xs" />
                          <span className="min-w-0 flex-1 truncate text-slate-700">
                            {p.name}
                            {isSelf && <span className="text-slate-400"> (you)</span>}
                          </span>
                          {isCreator && <Crown className="h-3 w-3 shrink-0 text-amber-500" aria-label="Started this discussion" />}
                          {canRemove && (
                            <button
                              type="button"
                              onClick={() => handleRemoveParticipant(p.userId)}
                              disabled={removeParticipant.isPending}
                              className="shrink-0 rounded p-0.5 text-slate-300 hover:bg-red-50 hover:text-red-500 disabled:opacity-50"
                              aria-label={isSelf ? "Leave this discussion" : `Remove ${p.name}`}
                            >
                              <UserMinus className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-between border-b border-slate-100 px-3 py-2.5">
              <p className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                <span className="flex h-6 w-6 items-center justify-center rounded-lg bg-gradient-to-br from-brand-500 to-violet-600 text-white shadow-sm shadow-brand-500/40">
                  <MessageCircle className="h-3.5 w-3.5" />
                </span>
                Messages
              </p>
              <div className="flex items-center gap-1">
                {view.screen === "list" ? (
                  <>
                    {(conversations ?? []).length > 0 && (
                      <button
                        onClick={() => (selecting ? stopSelecting() : setSelecting(true))}
                        className={clsx("rounded-md p-1 transition", selecting ? "bg-brand-50 text-brand-600" : "text-slate-400 hover:bg-slate-100")}
                        aria-label={selecting ? "Stop selecting" : "Select conversations"}
                        title={selecting ? "Stop selecting" : "Select to delete"}
                      >
                        <CheckSquare className="h-4 w-4" />
                      </button>
                    )}
                    <button onClick={() => setView({ screen: "new" })} className="rounded-md p-1 text-slate-400 hover:bg-slate-100" aria-label="New conversation">
                      <Plus className="h-4 w-4" />
                    </button>
                  </>
                ) : (
                  <button onClick={() => setView({ screen: "list" })} className="rounded-md p-1 text-slate-400 hover:bg-slate-100" aria-label="Back to conversations">
                    <ArrowLeft className="h-4 w-4" />
                  </button>
                )}
                {sizeControl}
                <button onClick={() => setOpen(false)} className="rounded-md p-1 text-slate-400 hover:bg-slate-100" aria-label="Close chat">
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}

          {/* request 1191: select all / some and delete — only from my inbox */}
          {view.screen === "list" && selecting && (conversations ?? []).length > 0 && (() => {
            const all = (conversations as any[]).map((c) => c.id as string);
            const allOn = all.every((id) => picked.has(id));
            const someOn = picked.size > 0 && !allOn;
            return (
              <div className="cl-row flex items-center gap-2 border-b border-slate-100 bg-gradient-to-r from-brand-50 to-white px-3 py-2">
                <label className="flex cursor-pointer items-center gap-2 text-xs font-semibold text-slate-700">
                  <input
                    type="checkbox"
                    checked={allOn}
                    ref={(el) => {
                      if (el) el.indeterminate = someOn;
                    }}
                    onChange={() => setPicked(allOn ? new Set() : new Set(all))}
                    className="h-4 w-4 accent-brand-600"
                  />
                  Select all
                </label>
                <span className="text-[11px] text-slate-500">{picked.size ? `${picked.size} selected` : `${all.length} conversations`}</span>
                <span className="ml-auto flex items-center gap-1.5">
                  <button onClick={stopSelecting} className="rounded-md px-2 py-1 text-[11px] font-medium text-slate-500 hover:bg-slate-100">
                    Cancel
                  </button>
                  <button
                    onClick={() => setPendingBulkDelete(true)}
                    disabled={!picked.size}
                    className="flex items-center gap-1 rounded-md bg-red-600 px-2 py-1 text-[11px] font-semibold text-white shadow-sm transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <Trash2 className="h-3 w-3" /> Delete{picked.size ? ` (${picked.size})` : ""}
                  </button>
                </span>
              </div>
            );
          })()}
          {view.screen === "list" && (
            <div className="flex-1 overflow-y-auto">
              {conversationsLoading && (
                <div className="flex justify-center py-8">
                  <Spinner className="h-5 w-5" />
                </div>
              )}
              {!conversationsLoading && (conversations ?? []).length === 0 && (
                <div className="p-6">
                  <EmptyState title="No conversations yet" description="Start one with the + button above." />
                </div>
              )}
              {(conversations ?? []).map((c: any, i: number) => (
                <button
                  key={c.id}
                  onClick={() =>
                    selecting
                      ? setPicked((p) => {
                          const n = new Set(p);
                          if (n.has(c.id)) n.delete(c.id);
                          else n.add(c.id);
                          return n;
                        })
                      : openConversation(c.id, c.title, c.isGroup)
                  }
                  className={clsx(
                    "cl-row flex w-full items-center gap-2.5 border-b border-slate-50 px-3 py-2.5 text-left transition hover:bg-gradient-to-r hover:from-brand-50/70 hover:to-transparent",
                    selecting && picked.has(c.id) ? "bg-brand-50" : c.unreadCount > 0 && "bg-brand-50/40"
                  )}
                  style={{ animationDelay: `${Math.min(i, 12) * 35}ms` }}
                >
                  {selecting && (
                    <span className={clsx("flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-all duration-200", picked.has(c.id) ? "scale-110 border-brand-600 bg-brand-600 text-white" : "border-slate-300 bg-white")}>
                      {picked.has(c.id) && <Check className="h-3 w-3" />}
                    </span>
                  )}
                  <Avatar name={c.title} size="sm" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <p className="truncate text-sm font-medium text-slate-800">{c.title}</p>
                      {c.lastMessage && (
                        <span className="shrink-0 text-[11px] text-slate-400">
                          {formatDistanceToNow(new Date(c.lastMessage.createdAt), { addSuffix: true })}
                        </span>
                      )}
                    </div>
                    <p className="truncate text-xs text-slate-500">
                      {c.lastMessage
                        ? c.lastMessage.kind === "CARD"
                          ? "📋 Discussion started"
                          : c.lastMessage.body ?? (c.lastMessage.hasAttachments ? "📎 Attachment" : "")
                        : "No messages yet"}
                    </p>
                  </div>
                  {c.unreadCount > 0 && (
                    <span className="flex h-5 min-w-[20px] shrink-0 items-center justify-center rounded-full bg-brand-600 px-1.5 text-[11px] font-semibold text-white">
                      {c.unreadCount > 99 ? "99+" : c.unreadCount}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}

          {view.screen === "new" && (
            <div className="flex flex-1 flex-col overflow-hidden">
              <div className="border-b border-slate-100 p-2">
                <div className="relative">
                  <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                  <input
                    autoFocus
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    placeholder="Search people…"
                    className="w-full rounded-lg border border-slate-200 bg-slate-50 py-1.5 pl-8 pr-2 text-sm text-slate-700 placeholder:text-slate-400 focus-visible:focus-ring"
                  />
                </div>
              </div>
              <div className="flex-1 overflow-y-auto">
                {(messageableUsers ?? []).length === 0 && (
                  <div className="p-6">
                    <EmptyState title="No people found" />
                  </div>
                )}
                {(messageableUsers ?? []).map((u: any) => (
                  <button
                    key={u.id}
                    onClick={() => handleStartConversation(u.id, u.name)}
                    disabled={startConversation.isPending}
                    className="flex w-full items-center gap-2.5 border-b border-slate-50 px-3 py-2.5 text-left hover:bg-slate-50 disabled:opacity-60"
                  >
                    <Avatar name={u.name} size="sm" />
                    <p className="truncate text-sm font-medium text-slate-800">{u.name}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {view.screen === "thread" && (
            <>
              <div className="flex-1 space-y-2 overflow-y-auto p-3">
                {messagesLoading && (
                  <div className="flex justify-center py-8">
                    <Spinner className="h-5 w-5" />
                  </div>
                )}
                {(messages ?? []).map((m: any) => {
                  // See animate-message-in/-glow in index.css.
                  const isNew = newMessageIds.has(m.id);
                  if (m.kind === "CARD" && m.metadata) {
                    const card = m.metadata as { entityType: "TASK" | "BOARD"; code: string; title: string; subtitle?: string; path: string };
                    return (
                      <div key={m.id} className={clsx("group flex justify-center", isNew && "animate-message-in")}>
                        <Link
                          to={card.path}
                          className="flex w-full max-w-[90%] items-start gap-2 rounded-lg border border-brand-200 bg-brand-50 px-3 py-2 text-left hover:bg-brand-100"
                        >
                          <span className="mt-0.5 shrink-0 text-brand-600">
                            {card.entityType === "TASK" ? <ListTodo className="h-4 w-4" /> : <Trello className="h-4 w-4" />}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="flex items-center gap-1 text-[11px] font-medium text-brand-500">
                              {card.code}
                              {card.subtitle && <span>· {card.subtitle}</span>}
                            </span>
                            <span className="block truncate text-sm font-medium text-slate-800">{card.title}</span>
                          </span>
                          <ExternalLink className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand-400 opacity-0 transition-opacity group-hover:opacity-100" />
                        </Link>
                      </div>
                    );
                  }
                  const isMine = m.sender.id === user.id;
                  // In a group, say who each message is from — otherwise there's no telling the members apart.
                  const showSenderName = !!view.isGroup && !isMine;
                  const sticker = isStickerOnly(m.body) && !(m.attachments?.length > 0);
                  const isEditing = editingId === m.id;
                  const canEdit = isMine && !(m.attachments?.length > 0) && Date.now() - new Date(m.createdAt).getTime() < EDIT_WINDOW_MS;
                  return (
                    <div key={m.id} className={clsx("group flex items-end gap-1", isMine ? "justify-end" : "justify-start", isNew && "animate-message-in")}>
                      {isMine && !isEditing && (
                        <div className="mb-1 flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
                          {canEdit && (
                            <button
                              onClick={() => startEdit(m)}
                              className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                              aria-label="Edit message"
                            >
                              <Pencil className="h-3 w-3" />
                            </button>
                          )}
                          <button
                            onClick={() => setPendingDeleteMessageId(m.id)}
                            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-red-500"
                            aria-label="Delete message"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </div>
                      )}
                      {isEditing ? (
                        <div className="max-w-[80%] flex-1 rounded-xl border border-slate-200 bg-white p-2">
                          <textarea
                            autoFocus
                            value={editDraft}
                            onChange={(e) => setEditDraft(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && !e.shiftKey) {
                                e.preventDefault();
                                saveEdit(m.id);
                              } else if (e.key === "Escape") {
                                cancelEdit();
                              }
                            }}
                            rows={1}
                            className="w-full resize-none border-none p-0 text-sm text-slate-800 focus:outline-none focus:ring-0"
                          />
                          <div className="mt-1 flex justify-end gap-1">
                            <button onClick={cancelEdit} className="rounded px-2 py-0.5 text-xs text-slate-500 hover:bg-slate-100">
                              Cancel
                            </button>
                            <button
                              onClick={() => saveEdit(m.id)}
                              disabled={!editDraft.trim() || editMessage.isPending}
                              className="rounded bg-brand-600 px-2 py-0.5 text-xs text-white hover:bg-brand-700 disabled:opacity-50"
                            >
                              Save
                            </button>
                          </div>
                        </div>
                      ) : sticker ? (
                        <div className="max-w-[80%]">
                          {showSenderName && <p className="mb-0.5 text-xs font-semibold text-brand-600">{m.sender.name}</p>}
                          <p className="text-4xl leading-none">{m.body}</p>
                          <p className={clsx("mt-1 text-[10px] text-slate-400", isMine ? "text-right" : "text-left")}>
                            {m.editedAt && <span className="italic">edited · </span>}
                            {formatDistanceToNow(new Date(m.createdAt), { addSuffix: true })}
                          </p>
                        </div>
                      ) : (
                        <div
                          className={clsx(
                            "max-w-[80%] rounded-xl px-3 py-2 text-sm",
                            isMine ? "bg-brand-600 text-white" : "bg-slate-100 text-slate-800",
                            isNew && !isMine && "animate-message-glow"
                          )}
                        >
                          {showSenderName && <p className="mb-0.5 text-xs font-semibold text-brand-600">{m.sender.name}</p>}
                          {m.body && <p className="whitespace-pre-wrap break-words">{m.body}</p>}
                          {m.attachments?.length > 0 && (
                            <div className={clsx("space-y-1", m.body && "mt-1.5")}>
                              {m.attachments.map((a: any) => (
                                <button
                                  key={a.id}
                                  onClick={() => downloadChatAttachment(a.id, a.fileName)}
                                  className={clsx(
                                    "flex w-full items-center gap-1.5 rounded-lg px-2 py-1.5 text-left text-xs",
                                    isMine ? "bg-white/15 hover:bg-white/25" : "bg-white hover:bg-slate-50"
                                  )}
                                >
                                  <Download className="h-3 w-3 shrink-0" />
                                  <span className="min-w-0 flex-1 truncate">{a.fileName}</span>
                                  <span className={clsx("shrink-0", isMine ? "text-white/70" : "text-slate-400")}>{formatBytes(a.fileSizeBytes)}</span>
                                </button>
                              ))}
                            </div>
                          )}
                          <p className={clsx("mt-1 text-right text-[10px]", isMine ? "text-white/70" : "text-slate-400")}>
                            {m.editedAt && <span className="italic">edited · </span>}
                            {formatDistanceToNow(new Date(m.createdAt), { addSuffix: true })}
                          </p>
                        </div>
                      )}
                      {!isMine && !isEditing && (
                        <div className="mb-1 flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
                          <button
                            onClick={() => setPendingDeleteMessageId(m.id)}
                            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-red-500"
                            aria-label="Delete message"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>

              <form onSubmit={submitMessage} className="border-t border-slate-100 p-2">
                {pendingFiles.length > 0 && (
                  <div className="mb-1.5 flex flex-wrap gap-1.5">
                    {pendingFiles.map((f, i) => (
                      <span key={i} className="flex items-center gap-1 rounded-full bg-slate-100 px-2 py-1 text-[11px] text-slate-600">
                        {f.name}
                        <button type="button" onClick={() => setPendingFiles((prev) => prev.filter((_, idx) => idx !== i))} aria-label={`Remove ${f.name}`}>
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <div className="relative flex items-end gap-1.5">
                  {showStickers && (
                    <div ref={stickerRef} className="absolute bottom-full left-0 z-10 mb-1.5 grid w-56 grid-cols-7 gap-1 rounded-lg border border-slate-200 bg-white p-2 shadow-lg">
                      {STICKERS.map((s) => (
                        <button
                          key={s}
                          type="button"
                          onClick={() => insertSticker(s)}
                          className="rounded-md p-1 text-lg hover:bg-slate-100"
                          aria-label={`Insert ${s}`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  )}
                  <input ref={fileInputRef} type="file" multiple className="hidden" onChange={onFilesSelected} />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="shrink-0 rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                    aria-label="Attach files"
                  >
                    <Paperclip className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowStickers((s) => !s)}
                    className="shrink-0 rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                    aria-label="Stickers"
                  >
                    <Smile className="h-4 w-4" />
                  </button>
                  <textarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        submitMessage(e as unknown as React.FormEvent);
                      }
                    }}
                    rows={1}
                    placeholder="Type a message…"
                    className="max-h-24 flex-1 resize-none rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 placeholder:text-slate-400 focus-visible:focus-ring"
                  />
                  <button
                    type="submit"
                    disabled={sendMessage.isPending || (!draft.trim() && pendingFiles.length === 0)}
                    className="shrink-0 rounded-lg bg-brand-600 p-2 text-white hover:bg-brand-700 disabled:opacity-50"
                    aria-label="Send message"
                  >
                    <Send className="h-4 w-4" />
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      )}

      {/* request 1189: a peek at the message that just arrived — click to open it */}
      {!open && peek && (
        <div key={peek.id + burst} className="cl-peek absolute bottom-full right-0 mb-3 w-72">
          <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl shadow-brand-900/10">
            <span className="cl-shimmer absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-brand-500 via-pink-500 to-cyan-400" />
            <button
              onClick={() => {
                setPeekWanted(false);
                setOpen(true);
                openConversation(peek.id, peek.title, peek.isGroup);
              }}
              className="flex w-full items-start gap-2.5 px-3 pb-3 pt-3.5 text-left"
            >
              <span className="relative shrink-0">
                <Avatar name={peek.title} size="sm" />
                <span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-emerald-500 ring-2 ring-white" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex items-center justify-between gap-2">
                  <span className="truncate text-[13px] font-semibold text-slate-900">{peek.title}</span>
                  <span className="shrink-0 rounded-full bg-brand-50 px-1.5 py-0.5 text-[10px] font-semibold text-brand-700">{peek.unread} new</span>
                </span>
                <span className="relative mt-0.5 block h-4">
                  <span className="absolute left-0 top-1 flex gap-1 transition-opacity duration-300" style={{ animation: "cl-reveal .3s ease-in 1.5s reverse both" }}>
                    {[0, 1, 2].map((i) => (
                      <span key={i} className="cl-dot h-1.5 w-1.5 rounded-full bg-brand-400" style={{ animationDelay: `${i * 0.15}s` }} />
                    ))}
                  </span>
                  <span className="cl-reveal absolute inset-0 truncate text-[12px] text-slate-600">{peek.text}</span>
                </span>
              </span>
            </button>
            <button onClick={() => setPeekWanted(false)} className="absolute right-1.5 top-2.5 rounded-full p-0.5 text-slate-300 hover:bg-slate-100 hover:text-slate-500" aria-label="Dismiss">
              <X className="h-3 w-3" />
            </button>
          </div>
          <span className="absolute -bottom-1.5 right-6 h-3 w-3 rotate-45 border-b border-r border-slate-200 bg-white" />
        </div>
      )}

      <ChatLauncher open={open} unread={unreadCount ?? 0} burst={burst} onClick={() => setOpen((o) => !o)} />

      <ConfirmDialog
        open={!!pendingDeleteMessageId}
        title="Delete message"
        message={`This will remove the message from your view only — ${view.screen === "thread" ? (view.isGroup ? "the rest of the group" : view.title) : "the other person"} will still see it.`}
        confirmLabel="Delete for me"
        loading={deleteMessage.isPending}
        onConfirm={confirmDeleteMessage}
        onCancel={() => setPendingDeleteMessageId(null)}
      />
      <ConfirmDialog
        open={pendingBulkDelete}
        title={`Delete ${picked.size} conversation${picked.size === 1 ? "" : "s"}`}
        message="This clears them from your inbox only — the other people still see them, and a conversation comes back for you if a new message arrives in it."
        confirmLabel="Delete for me"
        loading={deleteConversations.isPending}
        onConfirm={confirmBulkDelete}
        onCancel={() => setPendingBulkDelete(false)}
      />
      <ConfirmDialog
        open={pendingDeleteConversation}
        title={view.screen === "thread" && view.isGroup ? "Leave this view of the discussion" : "Delete conversation"}
        message={`This removes ${view.screen === "thread" && view.isGroup ? `"${view.title}"` : `your conversation with ${view.screen === "thread" ? view.title : "this person"}`} from your inbox only — they'll still see it, and it'll come back for you if a new message arrives.`}
        confirmLabel="Delete for me"
        loading={deleteConversation.isPending}
        onConfirm={confirmDeleteConversation}
        onCancel={() => setPendingDeleteConversation(false)}
      />
    </div>
  );
};
